import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface PovertyByRaceTrendsProps {
  location: string;
}

// Time-series of poverty percentage by race (lines by race, years on x-axis)
const PovertyByRaceTrends: React.FC<PovertyByRaceTrendsProps> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [hasData, setHasData] = React.useState<boolean | null>(null);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Pad geofips with leading zeros to match database format
    // Cities (place) are 6 digits -> 7 digits with leading zero
    // Counties are 4 digits -> 5 digits with leading zero
    let geoid = String(geofips);
    if (geoid.length === 6) {
      geoid = '0' + geoid;
    } else if (geoid.length === 4) {
      geoid = '0' + geoid;
    }

    // Fetch poverty by race by year
    const { data, error } = await supabaseClient
      .from('poverty_by_race')
      .select('year, race, percentage')
      .eq('geoid', geoid)
      .order('year', { ascending: true });

    if (error) {
      console.warn('PovertyByRaceTrends: Error fetching poverty_by_race:', error);
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    if (!data || data.length === 0) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    setHasData(true);

    // Group by race -> { race: { year: percentage } }
    const byRace: Record<string, Record<number, number>> = {};
    const yearsSet = new Set<number>();
    (data || []).forEach((r: any) => {
      const race = r.race || 'Unknown';
      const year = Number(r.year);
      const pct = Number(r.percentage || 0);
      yearsSet.add(year);
      if (!byRace[race]) byRace[race] = {};
      byRace[race][year] = pct;
    });

    const years = Array.from(yearsSet).sort((a, b) => a - b);

    // Some consistent color palette
    const colors = [
      '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
      '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
    ];

    const races = Object.keys(byRace).sort();
    const datasets = races.map((race, idx) => {
      const color = colors[idx % colors.length];
      return {
        label: race,
        data: years.map(y => byRace[race][y] ?? null),
        borderColor: color,
        backgroundColor: 'transparent',
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 2,
      } as const;
    });

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Poverty Rates by Race - ${location}`),
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(1)}%`,
            }
          }
        },
        scales: {
          y: {
            title: { display: true, text: 'Poverty Rate (%)' },
            beginAtZero: true,
            suggestedMax: 35,
          },
          x: {
            title: { display: true, text: 'Year' },
          },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });

    creatingRef.current = false;
  }, [location]);

  useEffect(() => {
    buildChart();
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasRef.current) {
          const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch {}
    };
  }, [buildChart]);

  if (hasData === false) {
    return (
      <div className="chart-container" style={{ padding: '40px', textAlign: 'center' }}>
        <p>No poverty by race time series data available for {location}.</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} style={{ height: '360px' }} />
    </div>
  );
};

export default PovertyByRaceTrends;


